import { useEffect, useState } from 'react'
import { adminApi } from '../../api/adminApi'
import { ReferralFeePreset } from '../../types'
import { toast } from 'react-hot-toast'
import { Loader2, Plus, Trash2, Bell } from 'lucide-react'

export default function AdminSettingsPage() {
  const [presets, setPresets] = useState<ReferralFeePreset[]>([])
  const [loading, setLoading] = useState(true)
  const [broadcastTitle, setBroadcastTitle] = useState('')
  const [broadcastBody, setBroadcastBody] = useState('')
  const [broadcastLoading, setBroadcastLoading] = useState(false)
  const [newPreset, setNewPreset] = useState({ label: '', amountPaise: 0 })
  const [presetLoading, setPresetLoading] = useState(false)

  useEffect(() => {
    adminApi
      .getFeePresets()
      .then(({ data }) => setPresets(data.data || []))
      .catch(() => toast.error('Failed to load presets'))
      .finally(() => setLoading(false))
  }, [])

  const handleTogglePreset = async (preset: ReferralFeePreset) => {
    try {
      await adminApi.toggleFeePreset(preset.id)
      setPresets((prev) =>
        prev.map((p) => (p.id === preset.id ? { ...p, isActive: !p.isActive } : p))
      )
    } catch {
      toast.error('Failed to update preset')
    }
  }

  const handleDeletePreset = async (id: number) => {
    try {
      await adminApi.deleteFeePreset(id)
      setPresets((prev) => prev.filter((p) => p.id !== id))
      toast.success('Preset deleted')
    } catch {
      toast.error('Failed to delete preset')
    }
  }

  const handleAddPreset = async () => {
    if (!newPreset.label.trim() || newPreset.amountPaise < 0) return
    setPresetLoading(true)
    try {
      const { data } = await adminApi.createFeePreset(newPreset)
      setPresets((prev) => [...prev, data.data!])
      setNewPreset({ label: '', amountPaise: 0 })
      toast.success('Preset created')
    } catch {
      toast.error('Failed to create preset')
    } finally {
      setPresetLoading(false)
    }
  }

  const handleBroadcast = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!broadcastTitle.trim() || !broadcastBody.trim()) return
    setBroadcastLoading(true)
    try {
      await adminApi.broadcastNotification({ title: broadcastTitle, body: broadcastBody })
      toast.success('Notification broadcast sent!')
      setBroadcastTitle('')
      setBroadcastBody('')
    } catch {
      toast.error('Failed to broadcast')
    } finally {
      setBroadcastLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      {/* Fee Presets */}
      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-4">Referral Fee Presets</h2>

        <div className="space-y-2 mb-4">
          {presets.map((preset) => (
            <div key={preset.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleTogglePreset(preset)}
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    preset.isActive ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                  }`}
                >
                  {preset.isActive && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
                <span className="font-medium text-gray-900">{preset.label}</span>
                <span className="text-sm text-gray-500">₹{preset.amountPaise / 100}</span>
              </div>
              <button
                onClick={() => handleDeletePreset(preset.id)}
                className="p-1 hover:bg-red-50 hover:text-red-500 rounded transition-colors text-gray-400"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Add new preset */}
        <div className="flex gap-2 border-t border-gray-100 pt-4">
          <input
            type="text"
            value={newPreset.label}
            onChange={(e) => setNewPreset((p) => ({ ...p, label: e.target.value }))}
            className="input flex-1"
            placeholder="Label (e.g. ₹100)"
          />
          <input
            type="number"
            value={newPreset.amountPaise / 100}
            onChange={(e) => setNewPreset((p) => ({ ...p, amountPaise: Number(e.target.value) * 100 }))}
            className="input w-28"
            placeholder="Amount ₹"
            min={0}
          />
          <button
            onClick={handleAddPreset}
            disabled={presetLoading}
            className="btn-primary px-3 flex items-center gap-1"
          >
            {presetLoading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add
          </button>
        </div>
      </div>

      {/* Broadcast Notification */}
      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell size={18} /> Broadcast Notification
        </h2>
        <form onSubmit={handleBroadcast} className="space-y-3">
          <input
            type="text"
            value={broadcastTitle}
            onChange={(e) => setBroadcastTitle(e.target.value)}
            className="input"
            placeholder="Notification title"
          />
          <textarea
            value={broadcastBody}
            onChange={(e) => setBroadcastBody(e.target.value)}
            rows={3}
            className="input resize-none"
            placeholder="Notification message..."
          />
          <button
            type="submit"
            disabled={broadcastLoading || !broadcastTitle.trim() || !broadcastBody.trim()}
            className="btn-primary"
          >
            {broadcastLoading ? 'Sending...' : 'Broadcast to All Users'}
          </button>
        </form>
      </div>
    </div>
  )
}
